import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/Button";
import { Drawer } from "../../../components/Drawer";
import { Input } from "../../../components/Input";
import { UpdateKanbanDto } from "../../../model/kanban/dto/UpdateKanbanDto";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { getTranslatedKanbanName } from "../KanbanUtils";
import { useDrawersContext } from "../../tasks/drawers-context";
import { KanbanClosingReason } from "../../../model/kanban/types/KanbanClosingReason";
import { KanbanClosingReasonsSettings } from "./KanbanClosingReasonsSettings";
import { TestIDs } from "../../../tests/types/testIds";
import isEqual from "lodash/isEqual";

export const KanbanSettingsDrawer: React.FC = () => {
  const [updateLoading, setUpdateLoading] = useState(false);
  const [closingReasons, setClosingReasons] = useState<KanbanClosingReason[]>(
    [],
  );

  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const isMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const kanbansMeta = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);
  const canRename = kanbansMeta?.length > 1;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { kanbanSettingsVisible, setKanbanSettingsVisible } =
    useDrawersContext();
  const { control, getValues, reset, setError, handleSubmit } =
    useForm<UpdateKanbanDto>();

  const hasCards = useMemo(() => {
    return kanban?.columns?.some((col) => Boolean(col?.cards?.length));
  }, [kanban]);

  const kanbanName = getTranslatedKanbanName(kanban?.name || "");

  const onUpdateKanban = async () => {
    try {
      setUpdateLoading(true);

      if (canRename) {
        await dispatch(
          kanbanActions.updateKanban({
            userId,
            kanbanId: kanban!._id,
            name: getValues("name"),
            ...(!hasCards ? { template: getValues("template") } : {}),
          }),
        ).unwrap();
      }

      const reasonsToSave = closingReasons
        .map((r) => ({ ...r, label: r.label.trim() }))
        .filter((r) => r.label.length);

      if (!isMember && !isEqual(reasonsToSave, kanban?.closingReasons || [])) {
        await dispatch(
          kanbanActions.updateKanbanClosingReasons({
            userId,
            kanbanId: kanban!._id,
            closingReasons: reasonsToSave,
          }),
        ).unwrap();
      }

      await Promise.all([
        dispatch(kanbanActions.getUserKanbansMeta({ userId })).unwrap(),
        dispatch(
          kanbanActions.getUserKanban({ userId, kanbanId: kanban!._id }),
        ).unwrap(),
      ]);

      AtiraToast.success(t("kanban.settings.drawer.update.success"));
      setKanbanSettingsVisible(false);
    } catch (e: any) {
      onSetTypedCRUDErrors(e, setError);
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setUpdateLoading(false);
    }
  };

  useEffect(() => {
    if (kanban) {
      reset({
        name: kanbanName,
      });
    }
  }, [kanban, reset, kanbanName]);

  useEffect(() => {
    if (kanbanSettingsVisible) {
      setClosingReasons(kanban?.closingReasons || []);
    }
  }, [kanbanSettingsVisible, kanban?.closingReasons]);

  return (
    <Drawer
      open={kanbanSettingsVisible}
      onClose={() => setKanbanSettingsVisible(false)}
      title={t("kanban.settings.drawer.title", { name: kanbanName })}
      size="large"
    >
      {canRename ? (
        <Controller
          control={control}
          name="name"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              title={t("kanban.settings.drawer.name.input.title")}
              onChange={onChange}
              value={value}
              valid={!error}
              errorMessage={error?.message}
            />
          )}
        />
      ) : null}

      {kanban && !isMember ? (
        <KanbanClosingReasonsSettings
          kanban={kanban}
          value={closingReasons}
          onChange={setClosingReasons}
        />
      ) : null}

      <Button
        width="100%"
        onClick={handleSubmit(onUpdateKanban)}
        loading={updateLoading}
        title={t("common.save")}
        dataTestId={TestIDs.kanbans.settings.save}
      />
    </Drawer>
  );
};
